import type { CostEstimateData, CostItem, MajorWork, MidWork } from "./types";

/** 품목 금액 = 수량 × 단가 */
export const itemAmount = (item: CostItem): number =>
  item.quantity * item.unitPrice;

/** 중공종 소계 */
export const midWorkTotal = (mid: MidWork): number =>
  mid.items.reduce((sum, item) => sum + itemAmount(item), 0);

/** 대공종 소계 */
export const majorWorkTotal = (major: MajorWork): number =>
  major.midWorks.reduce((sum, mid) => sum + midWorkTotal(mid), 0);

/** 비목 전체 합계 (대공종 리스트) */
export const majorWorksTotal = (majors: MajorWork[]): number =>
  majors.reduce((sum, major) => sum + majorWorkTotal(major), 0);

/**
 * 두 비목(재료/노무)의 대공사 구조를 같은 코드끼리 합쳐 하나로 — 집계표/미리보기용.
 * 같은 중공사 코드면 품목을 한 표에 모음 (품명으로 재료/노무/경비 자동 분류).
 */
export const mergeWorks = (a: MajorWork[], b: MajorWork[]): MajorWork[] => {
  const order: string[] = [];
  const byCode = new Map<string, MajorWork>();
  const addAll = (majors: MajorWork[]) => {
    for (const maj of majors) {
      let target = byCode.get(maj.code);
      if (!target) {
        target = { code: maj.code, name: maj.name, midWorks: [] };
        byCode.set(maj.code, target);
        order.push(maj.code);
      }
      for (const mid of maj.midWorks) {
        let tmid = target.midWorks.find((m) => m.code === mid.code);
        if (!tmid) {
          tmid = { code: mid.code, name: mid.name, items: [] };
          target.midWorks.push(tmid);
        }
        tmid.items.push(...mid.items);
      }
    }
  };
  addAll(a);
  addAll(b);
  return order
    .map((c) => byCode.get(c))
    .filter((m): m is MajorWork => Boolean(m));
};

/** 계산된 경비 한 줄 */
export interface ComputedExpense {
  id: string;
  name: string;
  baseLabel: string;
  rate: number;
  amount: number;
}

/** 원가계산서 전체 계산 결과 */
export interface CostEstimateResult {
  directMaterial: number; // 직접재료비
  indirectMaterial: number; // 간접재료비
  materialSubtotal: number; // 재료비 소계
  directLabor: number; // 직접노무비
  indirectLabor: number; // 간접노무비
  laborSubtotal: number; // 노무비 소계
  expenses: ComputedExpense[]; // 경비 항목별
  expenseSubtotal: number; // 경비 소계
  netCost: number; // 순공사원가 = 계 (재료비+노무비+경비)
  generalAdmin: number; // 일반관리비
  profit: number; // 이윤
  wasteDisposal: number; // 폐기물처리비 (합계제외)
  supplyPrice: number; // 공급가액 (계+일반관리비+이윤)
  vat: number; // 부가가치세
  contractPrice: number; // 도급액 (공급가액+부가세)
  totalCost: number; // 총공사비 (도급액+폐기물처리비)
}

/**
 * 전체 계산. 요율(rate)을 바꾸면 이 함수만 다시 돌리면 됩니다.
 * 금액은 원 단위 반올림.
 */
export function computeCostEstimate(
  data: CostEstimateData,
): CostEstimateResult {
  const directMaterial = majorWorksTotal(data.directMaterial);
  const indirectMaterial = Math.round(
    directMaterial * data.indirectMaterialRate,
  );
  const materialSubtotal = directMaterial + indirectMaterial;

  const directLabor = majorWorksTotal(data.directLabor);
  const indirectLabor = Math.round(directLabor * data.indirectLaborRate);
  const laborSubtotal = directLabor + indirectLabor;

  const expenses: ComputedExpense[] = [];
  let priorExpenseSum = 0;
  for (const exp of data.expenses) {
    let baseValue: number;
    switch (exp.base) {
      case "labor":
        baseValue = laborSubtotal;
        break;
      case "materialLabor":
        baseValue = materialSubtotal + laborSubtotal;
        break;
      case "materialLaborExpensePrior":
        baseValue = materialSubtotal + laborSubtotal + priorExpenseSum;
        break;
    }
    const amount = Math.round(baseValue * exp.rate);
    priorExpenseSum += amount;
    expenses.push({
      id: exp.id,
      name: exp.name,
      baseLabel: exp.baseLabel,
      rate: exp.rate,
      amount,
    });
  }

  const expenseSubtotal = expenses.reduce((s, e) => s + e.amount, 0);
  // 계 (순공사원가)
  const netCost = materialSubtotal + laborSubtotal + expenseSubtotal;

  // 일반관리비 = 계 × 요율
  const generalAdmin = Math.round(netCost * data.generalAdminRate);
  // 이윤 = (노무비 + 경비 + 일반관리비) × 요율
  const profit = Math.round(
    (laborSubtotal + expenseSubtotal + generalAdmin) * data.profitRate,
  );
  // 폐기물처리비 (합계제외 — 공급가액에 포함하지 않음)
  const wasteDisposal = data.wasteDisposalAmount;
  // 공급가액 = 계 + 일반관리비 + 이윤
  const supplyPrice = netCost + generalAdmin + profit;
  // 부가가치세 = 공급가액 × 요율
  const vat = Math.round(supplyPrice * data.vatRate);
  // 도급액 = 공급가액 + 부가세
  const contractPrice = supplyPrice + vat;
  // 총공사비 = 도급액 + 폐기물처리비
  const totalCost = contractPrice + wasteDisposal;

  return {
    directMaterial,
    indirectMaterial,
    materialSubtotal,
    directLabor,
    indirectLabor,
    laborSubtotal,
    expenses,
    expenseSubtotal,
    netCost,
    generalAdmin,
    profit,
    wasteDisposal,
    supplyPrice,
    vat,
    contractPrice,
    totalCost,
  };
}

/** 품목 원가 유형 (집계표용) — 품명에 "(인건/노무)" → 노무, "(경비)" → 경비, 그 외 재료 */
export type CostType = "material" | "labor" | "expense";
export const itemCostType = (item: CostItem): CostType => {
  const n = item.name;
  if (n.includes("인건") || n.includes("노무")) return "labor";
  if (n.includes("경비")) return "expense";
  return "material";
};

/** 재료/노무/경비/합계 분해 */
export interface Breakdown {
  material: number;
  labor: number;
  expense: number;
  total: number;
}

export const itemBreakdown = (item: CostItem): Breakdown => {
  const amount = itemAmount(item);
  const type = itemCostType(item);
  return {
    material: type === "material" ? amount : 0,
    labor: type === "labor" ? amount : 0,
    expense: type === "expense" ? amount : 0,
    total: amount,
  };
};

const sumBreakdown = (parts: Breakdown[]): Breakdown =>
  parts.reduce(
    (a, b) => ({
      material: a.material + b.material,
      labor: a.labor + b.labor,
      expense: a.expense + b.expense,
      total: a.total + b.total,
    }),
    { material: 0, labor: 0, expense: 0, total: 0 },
  );

export const midWorkBreakdown = (mid: MidWork): Breakdown =>
  sumBreakdown(mid.items.map(itemBreakdown));

export const majorWorkBreakdown = (major: MajorWork): Breakdown =>
  sumBreakdown(major.midWorks.map(midWorkBreakdown));

/** 천 단위 콤마 (예: 1234567 → "1,234,567") */
export const formatWon = (n: number): string => n.toLocaleString("ko-KR");

/** 구성비 % (순공사원가 대비), 소수 1자리 */
export const compositionPct = (value: number, netCost: number): string =>
  netCost > 0 ? `${((value / netCost) * 100).toFixed(1)}%` : "0.0%";

/** 요율 표시 (0.0282 → "2.82%") — 불필요한 0 제거 */
export const formatRate = (rate: number): string => {
  const pct = rate * 100;
  return `${parseFloat(pct.toFixed(4))}%`;
};
